import React, { FC, useMemo } from 'react'
import { Questions } from '@mortgage-pos/types'
import { QuestionsList } from './QuestionsList'
import PlusIcon from '@mortgage-pos/ui/assets/PlusIcon.svg'
import MinusIcon from '@mortgage-pos/ui/assets/MinusIcon.svg'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import { parseCamelCase } from '@mortgage-pos/ui/utils/stringFormat'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { buildQuestionGroup } from '@mortgage-pos/ui/utils/questionHelpers'
import { getAnswersToVisibleQuestions } from '@mortgage-pos/ui/utils/pageHelpers'
import { useStoreState } from '@mortgage-pos/ui/store'

const REPEATABLE_QUESTION_GROUP_LIMIT = 50

interface FieldArrayHelpers {
  push: Function
  remove: Function
}

interface Props {
  title?: string
  subTitle?: string
  groupName: string
  questions: Questions[]
  formValues: object
  titleForRepeatButton: string
  textForRepeatButton: string
  textForDeleteButton: string
  autoRepeatValidationKey: string
  fieldArrayHelpers: FieldArrayHelpers
}

export const RepeatableQuestionGroup: FC<Props> = ({
  title,
  subTitle,
  groupName,
  questions,
  formValues,
  titleForRepeatButton,
  textForRepeatButton,
  textForDeleteButton,
  autoRepeatValidationKey,
  fieldArrayHelpers,
}) => {
  const prebuiltValidations = useStoreState(
    (state) => state.visibleIfConditions
  )

  const questionGroups = useMemo<object[]>(
    () => formValues[groupName],
    [formValues, groupName]
  )

  const repeatButtonText = useMemo<string>(
    () => textForRepeatButton || `Add another ${parseCamelCase(groupName)}`,
    [textForRepeatButton, groupName]
  )

  const deleteButtonText = useMemo<string>(
    () => textForDeleteButton || `Remove this ${parseCamelCase(groupName)}`,
    [textForDeleteButton, groupName]
  )

  const shouldShowAddGroupButton = useMemo<boolean>(() => {
    const lastQuestionGroup = questionGroups[questionGroups.length - 1]

    const answersToLastQuestionGroup = getAnswersToVisibleQuestions(
      questions,
      lastQuestionGroup,
      prebuiltValidations
    )

    // If the user has answered at least one question in the last question group,
    // display add button and allow to add another question group
    return Object.values(answersToLastQuestionGroup).some((answer) => {
      return answer !== '' && answer !== null && answer !== undefined
    })
  }, [questionGroups, questions])

  // Don't show remove button if there is only 1 questionGroup
  const showRemoveButton = questionGroups.length !== 1

  const handleAddQuestionGroup = () => {
    if (questionGroups.length < REPEATABLE_QUESTION_GROUP_LIMIT) {
      fieldArrayHelpers.push(buildQuestionGroup(questions))
    }
  }

  return (
    <>
      {questionGroups.map((groupValues, index) => {
        return (
          <div
            key={`repeatable-group-${groupName}-${index}`}
            className={`repeatable-group__group-wrapper ${
              index === 0 ? '--first' : ''
            }`}
          >
            <QuestionsList
              title={title}
              subTitle={subTitle}
              questionGroupName={groupName}
              questionGroupValues={groupValues}
              questionGroupIndex={index}
              questions={questions}
              customValidationKey={autoRepeatValidationKey}
              customValidationCallback={handleAddQuestionGroup}
            />

            {showRemoveButton ? (
              <button
                className="repeatable-group__remove"
                name="Remove"
                type="button"
                onClick={() => fieldArrayHelpers.remove(index)}
              >
                <img
                  className="repeatable-group__remove-icon"
                  src={MinusIcon}
                  alt="Remove icon"
                />
                {deleteButtonText}
              </button>
            ) : null}
          </div>
        )
      })}

      {!autoRepeatValidationKey && shouldShowAddGroupButton ? (
        <div className="repeatable-group__add">
          {titleForRepeatButton ? (
            <h2 className="repeatable-group__add-button-subtitle">
              {titleForRepeatButton}
            </h2>
          ) : null}
          <button
            className="repeatable-group__add-button"
            name="Add"
            type="button"
            onClick={handleAddQuestionGroup}
          >
            <img
              className="repeatable-group__add-icon"
              src={PlusIcon}
              alt="Add icon"
            />
            {repeatButtonText}
          </button>
        </div>
      ) : null}
    </>
  )
}

export default RepeatableQuestionGroup
